import React, { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  X,
  Send,
  Bot,
  User,
  Sparkles,
  ChevronLeft,
  ChevronRight,
  MessageCircle,
  Mic,
  MicOff,
  ArrowRight,
  RotateCcw
} from 'lucide-react'
import { useUser } from '../contexts/UserContext'
import { isAdminUser } from '../utils/userProfileFields'
import { customerMessage } from '../utils/customerError'
import { generateAIResponse } from '../services/aiService'
import { saveDesignHandoff } from '../utils/designHandoff'
import toast from 'react-hot-toast'

const QUICK_PROMPTS = [
  'Help me design a minimalist black hoodie',
  'What fabric works best for a summer dress?',
  'Help me plan a heavyweight crewneck',
  'Find a navy wool coat under $200'
]

const WELCOME = `Hi, I'm your **Style AI assistant**.

I can help you:
• **Design** custom garments (Custom or Design Studio)
• **Choose** fabrics, fits, colors, and construction details
• **Search** real products or **explore** community designs

Try a quick prompt below, or ask anything about fashion and this app.`

function buildNavigateUrl(path, designPrefill, searchPrefill) {
  const params = new URLSearchParams()
  if (designPrefill || searchPrefill) params.set('transfer', '1')
  params.set('_h', String(Date.now()))
  if (designPrefill) {
    if (designPrefill.type) params.set('type', designPrefill.type)
    if (designPrefill.material) params.set('material', designPrefill.material)
    if (designPrefill.color) params.set('color', designPrefill.color)
    if (designPrefill.style) params.set('style', designPrefill.style)
    if (designPrefill.notes) params.set('notes', designPrefill.notes)
  }
  if (searchPrefill) {
    if (searchPrefill.query) params.set('q', searchPrefill.query)
    if (searchPrefill.type) params.set('type', searchPrefill.type)
    if (searchPrefill.color) params.set('color', searchPrefill.color)
    if (searchPrefill.material) params.set('material', searchPrefill.material)
  }
  const qs = params.toString()
  return qs ? `${path}?${qs}` : path
}

function renderMarkdown(text) {
  if (!text) return null
  const parts = text.split(/(\*\*[^*]+\*\*)/g)
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i} className="font-semibold text-ivory">{part.slice(2, -2)}</strong>
    }
    return <span key={i}>{part}</span>
  })
}

const ChatPanel = ({ onClose, onPremiumClick, onAuthClick }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, isPremium } = useUser()
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'ai',
      content: WELCOME,
      timestamp: new Date(),
      actions: [],
      model: 'assistant'
    }
  ])
  const [inputValue, setInputValue] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [isListening, setIsListening] = useState(false)
  const [recognition, setRecognition] = useState(null)
  const [lastModel, setLastModel] = useState('assistant')
  const messagesEndRef = useRef(null)
  const inputRef = useRef(null)

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages, isTyping])

  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
      const recognitionInstance = new SpeechRecognition()
      recognitionInstance.continuous = false
      recognitionInstance.interimResults = false
      recognitionInstance.lang = 'en-US'
      recognitionInstance.onresult = (event) => {
        setInputValue(event.results[0][0].transcript)
        setIsListening(false)
      }
      recognitionInstance.onerror = () => setIsListening(false)
      recognitionInstance.onend = () => setIsListening(false)
      setRecognition(recognitionInstance)
    }
  }, [])

  const runAction = useCallback((action, designPrefill, searchPrefill) => {
    if (action.type !== 'navigate' || !action.path) return
    const url = buildNavigateUrl(action.path, designPrefill, searchPrefill)
    if (action.path === '/design' && designPrefill) {
      saveDesignHandoff({
        source: 'assistant',
        specifications: {
          type: designPrefill.type,
          material: designPrefill.material,
          color: designPrefill.color,
          style: designPrefill.style,
          quality: 'premium'
        },
        customInstructions: designPrefill.notes || ''
      })
    }
    navigate(url)
    toast.success(action.hint || `${action.label}: specs loaded`)
  }, [navigate])

  const sendMessage = async (text) => {
    const trimmed = text.trim()
    if (!trimmed || isTyping) return

    if (!user) {
      onAuthClick?.('signin')
      toast.error('Sign in to chat with the assistant')
      return
    }

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: trimmed,
      timestamp: new Date()
    }

    setMessages((prev) => [...prev, userMessage])
    setInputValue('')
    setIsTyping(true)

    try {
      const conversationHistory = [...messages, userMessage]
        .filter((msg) => msg.type === 'user' || (msg.type === 'ai' && msg.id !== 1))
        .slice(-20)
        .map((msg) => ({
          role: msg.type === 'user' ? 'user' : 'assistant',
          content: msg.content
        }))

      const result = await generateAIResponse(trimmed, conversationHistory, {
        isPremium,
        isSignedIn: !!user,
        currentPath: location.pathname,
        userName: user?.email || user?.displayName || ''
      })

      setLastModel(result.model || 'assistant')
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          type: 'ai',
          content: result.text,
          timestamp: new Date(),
          actions: result.actions || [],
          designPrefill: result.designPrefill,
          searchPrefill: result.searchPrefill,
          model: result.model
        }
      ])
    } catch (error) {
      console.error('AI Response Error:', error)
      if (error.code === 'AUTH_REQUIRED') {
        onAuthClick?.('signin')
        setMessages((prev) => [
          ...prev,
          {
            id: Date.now() + 1,
            type: 'ai',
            content: 'Please sign in to chat with the assistant.',
            timestamp: new Date(),
            actions: []
          }
        ])
        return
      }
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          type: 'ai',
          content: customerMessage(error, 'The assistant is unavailable right now. Please try again.', {
            isAdmin: isAdminUser(user)
          }),
          timestamp: new Date(),
          actions: [{ type: 'navigate', path: '/design', label: 'Open Custom' }]
        }
      ])
    } finally {
      setIsTyping(false)
      inputRef.current?.focus()
    }
  }

  const handleSendMessage = () => sendMessage(inputValue)

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  const handleVoiceInput = () => {
    if (!recognition) {
      toast.error('Speech recognition not supported in this browser')
      return
    }
    if (isListening) {
      recognition.stop()
      setIsListening(false)
    } else {
      recognition.start()
      setIsListening(true)
    }
  }

  const resetChat = () => {
    setMessages([
      {
        id: Date.now(),
        type: 'ai',
        content: WELCOME,
        timestamp: new Date(),
        actions: [],
        model: 'assistant'
      }
    ])
  }

  const renderMessageContent = (message) => {
    const content = message.content
    return (
      <div>
        <div className="whitespace-pre-wrap text-sm leading-relaxed text-ivory-muted">
          {renderMarkdown(content)}
        </div>
        {message.actions?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {message.actions.map((action, idx) => (
              <button
                key={`${action.path}-${idx}`}
                type="button"
                onClick={() => runAction(action, message.designPrefill, message.searchPrefill)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-gold/15 text-gold border border-gold/25 hover:bg-gold/25 transition-colors"
              >
                {action.label}
                <ArrowRight className="w-3 h-3" />
              </button>
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div
      className={`fixed inset-y-0 right-0 z-50 flex flex-col border-l border-ivory/10 bg-ink-soft shadow-2xl shadow-black/50 transition-all duration-300 ${
        isCollapsed ? 'w-14' : 'w-full sm:w-[400px]'
      }`}
    >
      <div className="flex items-center justify-between px-3 py-3 border-b border-ivory/10 shrink-0">
        {!isCollapsed && (
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-gold/15 border border-gold/20 flex items-center justify-center shrink-0">
              <Sparkles className="w-4 h-4 text-gold" />
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-ivory truncate">Style AI Assistant</h3>
              <p className="text-[10px] uppercase tracking-wider text-ivory-faint truncate">
                {lastModel === 'fallback' ? 'Offline mode' : 'Your design assistant'}
              </p>
            </div>
          </div>
        )}
        <div className="flex items-center gap-1">
          {!isCollapsed && (
            <button
              type="button"
              onClick={resetChat}
              className="p-2 text-ivory-faint hover:text-ivory rounded-lg hover:bg-ivory/10"
              title="New conversation"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          )}
          <button
            type="button"
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="p-2 text-ivory-faint hover:text-ivory rounded-lg hover:bg-ivory/10"
          >
            {isCollapsed ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
          {!isCollapsed && (
            <button type="button" onClick={onClose} className="p-2 text-ivory-faint hover:text-ivory rounded-lg hover:bg-ivory/10">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {!isCollapsed && (
        <>
          <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
            {messages.map((message) => (
              <div key={message.id} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[92%] rounded-2xl px-3.5 py-2.5 ${
                    message.type === 'user'
                      ? 'bg-gold/20 border border-gold/25 text-ivory'
                      : 'bg-ink border border-ivory/10'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    {message.type === 'ai' && (
                      <div className="w-6 h-6 rounded-full bg-gold/15 flex items-center justify-center shrink-0 mt-0.5">
                        <Bot className="w-3.5 h-3.5 text-gold" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      {renderMessageContent(message)}
                      <p className="text-[10px] text-ivory-faint mt-2 tabular-nums">
                        {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                    {message.type === 'user' && (
                      <div className="w-6 h-6 rounded-full bg-ivory/10 flex items-center justify-center shrink-0 mt-0.5">
                        <User className="w-3.5 h-3.5 text-ivory-muted" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {isTyping && (
              <div className="flex justify-start">
                <div className="rounded-2xl px-4 py-3 bg-ink border border-ivory/10 flex items-center gap-2">
                  <Bot className="w-4 h-4 text-gold animate-pulse" />
                  <span className="text-xs text-ivory-faint">Thinking…</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {messages.length <= 1 && (
            <div className="px-4 pb-2 flex flex-wrap gap-2 shrink-0">
              {QUICK_PROMPTS.map((prompt) => (
                <button
                  key={prompt}
                  type="button"
                  onClick={() => sendMessage(prompt)}
                  className="text-left text-[11px] px-2.5 py-1.5 rounded-lg border border-ivory/10 text-ivory-muted hover:border-gold/30 hover:text-gold transition-colors"
                >
                  {prompt}
                </button>
              ))}
            </div>
          )}

          <div className="p-3 border-t border-ivory/10 shrink-0">
            <div className="flex gap-2">
              <textarea
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder="Ask about design, fabrics, or Style AI…"
                className="flex-1 resize-none bg-ink border border-ivory/15 rounded-xl px-3 py-2 text-sm text-ivory placeholder:text-ivory-faint focus:outline-none focus:border-gold/40"
                rows={1}
                style={{ minHeight: '42px', maxHeight: '120px' }}
              />
              <button
                type="button"
                onClick={handleVoiceInput}
                disabled={isTyping}
                className={`p-2.5 rounded-xl border transition-colors ${
                  isListening
                    ? 'bg-gold/15 border-gold/40 text-gold'
                    : 'border-ivory/15 text-ivory-faint hover:text-ivory hover:border-ivory/25'
                }`}
              >
                {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
              </button>
              <button
                type="button"
                onClick={handleSendMessage}
                disabled={!inputValue.trim() || isTyping}
                className="p-2.5 rounded-xl bg-gold text-ink hover:bg-gold-light disabled:opacity-40 transition-colors"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </>
      )}

      {isCollapsed && (
        <div className="flex-1 flex items-center justify-center">
          <button
            type="button"
            onClick={() => setIsCollapsed(false)}
            className="w-10 h-10 rounded-xl bg-gold/15 flex items-center justify-center"
            title="Open assistant"
          >
            <MessageCircle className="w-5 h-5 text-gold" />
          </button>
        </div>
      )}
    </div>
  )
}

export default ChatPanel
